import type { ActivityDefinition, Extension } from "@medplum/fhirtypes";
import { asMemberId, canonicalLineageSchema, type StoredExamDefinitionProps } from "@repo/domain";
import { extensionUrl, FINDING_LINEAGE_SYSTEM } from "@repo/fhir-systems";
import { catalogModelKindTag, EXAM_DEFINITION_KIND } from "../lib/catalog-kind";
import { catalogNameToFhir } from "../lib/catalog-name";
import { required } from "./read";

// ============================================================
// ExamDefinition ⇄ ActivityDefinition
//
// Members are logical ObservationDefinition references by finding lineage.
// They float independently of the exam revision. Sections and prerequisites
// describe arrangement; clinical content remains in the finding registry.
// ============================================================

export const CATALOG_EXAM_DEFINITION_PROFILE = extensionUrl("catalog-exam-definition");
export const EXAM_DEFINITION_NOTE_LABEL_URL = extensionUrl("exam-definition-note-label");
export const EXAM_DEFINITION_MEMBER_MARKS_URL = extensionUrl("exam-definition-member-marks");
export const EXAM_DEFINITION_SECTION_URL = extensionUrl("exam-definition-section");
export const EXAM_FINDING_PREREQUISITE_URL = extensionUrl("exam-finding-prerequisite");
type Props = StoredExamDefinitionProps;
const OWNED = new Set<string>([
  EXAM_DEFINITION_NOTE_LABEL_URL,
  EXAM_DEFINITION_MEMBER_MARKS_URL,
  EXAM_DEFINITION_SECTION_URL,
]);
const ext = (extensions: Extension[] | undefined, url: string) =>
  extensions?.find((extension) => extension.url === url);

export function writeExamDefinitionRow(props: Props, prior: ActivityDefinition | null): ActivityDefinition {
  return {
    resourceType: "ActivityDefinition",
    ...(prior
      ? {}
      : { meta: { profile: [CATALOG_EXAM_DEFINITION_PROFILE], tag: [catalogModelKindTag(EXAM_DEFINITION_KIND)] } }),
    url: props.spine.url,
    version: String(props.spine.version),
    date: props.spine.mintedAt,
    ...catalogNameToFhir(props.spine.name),
    description: props.spine.description,
    status: props.spine.availability === "Active" ? "active" : "retired",
    observationResultRequirement: props.sections.flatMap((section) =>
      section.findings.map((finding) => ({
        type: "ObservationDefinition",
        identifier: { system: FINDING_LINEAGE_SYSTEM, value: finding.finding },
        extension: [
          { url: EXAM_DEFINITION_SECTION_URL, valueString: section.id },
          ...(finding.prerequisite
            ? [{ url: EXAM_FINDING_PREREQUISITE_URL, valueCanonical: finding.prerequisite }]
            : []),
        ],
      })),
    ),
    extension: [
      ...(prior?.extension ?? []).filter((extension) => !OWNED.has(extension.url)),
      ...props.sections.map((section) => ({
        url: EXAM_DEFINITION_SECTION_URL,
        extension: [
          { url: "id", valueString: section.id },
          { url: "label", valueString: section.label },
        ],
      })),
      ...(props.marks ? [{ url: EXAM_DEFINITION_MEMBER_MARKS_URL, valueInteger: props.marks.sections }] : []),
      ...(props.examNoteLabel === undefined
        ? []
        : [{ url: EXAM_DEFINITION_NOTE_LABEL_URL, valueString: props.examNoteLabel }]),
    ],
  };
}

export function readExamDefinitionRow(row: ActivityDefinition): Props {
  const noteLabel = ext(row.extension, EXAM_DEFINITION_NOTE_LABEL_URL)?.valueString;
  const marks = ext(row.extension, EXAM_DEFINITION_MEMBER_MARKS_URL)?.valueInteger;
  return {
    spine: {
      url: required(row, "url", row.url),
      version: Number(required(row, "version", row.version)),
      mintedAt: required(row, "mintedAt", row.date),
      name: required(row, "name", row.title),
      availability: row.status === "active" ? "Active" : "Inactive",
      ...(row.description === undefined ? {} : { description: row.description }),
    },
    sections: (row.extension ?? [])
      .filter((extension) => extension.url === EXAM_DEFINITION_SECTION_URL)
      .map((section) => {
        const id = required(row, "section.id", ext(section.extension, "id")?.valueString);
        return {
          id: asMemberId(id),
          label: required(row, "section.label", ext(section.extension, "label")?.valueString),
          findings: (row.observationResultRequirement ?? [])
            .filter((reference) => ext(reference.extension, EXAM_DEFINITION_SECTION_URL)?.valueString === id)
            .map((reference) => {
              const prerequisite = ext(reference.extension, EXAM_FINDING_PREREQUISITE_URL)?.valueCanonical;
              return {
                finding: required(
                  row,
                  "finding",
                  reference.identifier?.system === FINDING_LINEAGE_SYSTEM ? reference.identifier.value : undefined,
                ),
                ...(prerequisite ? { prerequisite: canonicalLineageSchema.parse(prerequisite) } : {}),
              };
            }),
        };
      }),
    ...(noteLabel === undefined ? {} : { examNoteLabel: noteLabel }),
    ...(marks === undefined ? {} : { marks: { sections: marks } }),
  };
}
